<template>
    <v-card flat color="transparent">
        <v-card-title>
            <span class="font-weight-bold">Welcome back {{ userDisplayName }}!</span>
        </v-card-title>
        <v-card-subtitle>What would you like to do today?</v-card-subtitle>
        <v-card-text>
            <v-list color="transparent">
                <v-subheader class="px-0 secondary--text text-uppercase">
                    <v-icon small color="amber" left>mdi-star</v-icon>
                    Favourites
                </v-subheader>

                <v-container fluid>
                    <v-row v-if="!favoriteSpacesError && !fetchingSpaces">
                        <v-col v-for="item in favoriteSpaces" :key="`favorite-space-${item.sid}`" cols="12" sm="12" md="6" lg="4" xl="3">
                            <SpaceCard :space="item" />
                        </v-col>
                    </v-row>

                    <v-row v-if="fetchingSpaces">
                        <v-col v-for="index in 3" :key="`favorite-skeleton-${index}`" cols="12" sm="12" md="6" lg="4" xl="3">
                            <v-skeleton-loader type="card" class="w-100"></v-skeleton-loader>
                        </v-col>
                    </v-row>

                    <div v-if="!fetchingSpaces && !favoriteSpaces.length && !favoriteSpacesError" class="pt-14">
                        <dashboard-empty icon="mdi-star-outline">
                            You have no favourite spaces yet.
                            <br />
                            Mark spaces as favourites to find them here quickly.
                        </dashboard-empty>
                    </div>

                    <div v-if="favoriteSpacesError" class="pt-14">
                        <dashboard-error />
                    </div>
                </v-container>

                <v-subheader class="px-0 mt-4 secondary--text text-uppercase">
                    <v-icon small color="secondary" left>mdi-monitor</v-icon>
                    Recent applications
                </v-subheader>

                <v-container fluid>
                    <v-row v-if="!$isError(recentApps)">
                        <v-col v-for="(item, index) in recentApps" :key="index" cols="12" sm="12" md="6" lg="4">
                            <DashboardQuickAccessCard :item="item" :loading="fetchingApps" />
                        </v-col>
                    </v-row>
                    <div v-if="!fetchingApps && !recentApps.length && !$isError(recentApps)" class="pt-14">
                        <dashboard-empty icon="mdi-information-outline">
                            No recent apps found.
                            <br />
                            Try adding a course or project first.
                        </dashboard-empty>
                    </div>
                    <div v-if="$isError(recentApps)" class="pt-14">
                        <dashboard-error />
                    </div>
                </v-container>

                <v-subheader class="px-0 mt-4 secondary--text text-uppercase">
                    <v-icon small color="secondary" left>mdi-view-grid-outline</v-icon>
                    Recent spaces
                </v-subheader>

                <AllSpacesTable :show-title="false" />
            </v-list>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapState } from 'vuex'

const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const DashboardError = () => import('@/components/DashboardError.vue')
const SpaceCard = () => import('@/components/SpaceCard.vue')
const DashboardQuickAccessCard = () => import('@/modules/home/components/DashboardQuickAccessCard.vue')
const AllSpacesTable = () => import('@/modules/home/components/AllSpacesTable.vue')

export default {
    name: 'HomeSpacesPanel',
    components: {
        DashboardEmpty,
        DashboardError,
        SpaceCard,
        DashboardQuickAccessCard,
        AllSpacesTable
    },
    computed: {
        ...mapState(['userMetadata', 'userInfo']),
        ...mapState('orgStore', ['educationSpaces', 'researchSpaces', 'datasetSpaces', 'fetchingSpaces']),
        ...mapState('appStore', ['recentApps', 'fetchingApps']),
        userDisplayName() {
            if (this.userInfo?.name) {
                return this.userInfo.name
            }
            if (this.userInfo?.given_name) {
                return this.userInfo.given_name
            }
            if (this.userInfo?.email) {
                return this.userInfo.email.split('@')[0]
            }
            return 'there'
        },
        allSpaces() {
            const spaces = [...this.educationSpaces, ...this.researchSpaces, ...this.datasetSpaces]
            const uniqueBySid = spaces.reduce((acc, space) => {
                if (space?.sid !== undefined && space?.sid !== null) {
                    acc[space.sid.toString()] = space
                }
                return acc
            }, {})
            return Object.values(uniqueBySid)
        },
        favoriteSpacesError() {
            return this.$isError(this.educationSpaces) || this.$isError(this.researchSpaces) || this.$isError(this.datasetSpaces)
        },
        favoriteSpaceSids() {
            if (!Array.isArray(this.userMetadata?.favorite_space_sids)) {
                return []
            }
            return this.userMetadata.favorite_space_sids.map(sid => sid.toString())
        },
        favoriteSpaces() {
            if (!this.favoriteSpaceSids.length) {
                return []
            }
            const spacesBySid = this.allSpaces.reduce((acc, space) => {
                acc[space.sid.toString()] = space
                return acc
            }, {})
            return this.favoriteSpaceSids.map(sid => spacesBySid[sid.toString()]).filter(Boolean)
        }
    }
}
</script>
